//TODO: Uses a TEMP copy of cwan-ux package component. Found here:
// https://gitlab.com/clearwateranalytics/apps/UIUX/cwan-ux/-/blob/master/packages/cwan-ux-core-react/src/components/TopNavigation.tsx

import { TopNavigation } from '@/components/Base/TEMP-TopNav/TopNavigation'
import DarkModeIcon from '@/components/ReportDashboard/Shared/DarkModeIcon'
import { FADashboardType } from '@/constants/ReportDashboard/dashboards/dashboard-types'
import { Dashboard } from '@/services/report-dashboard/dashboard.service'
import {
  DEFAULT_ATTRIBUTION_DASHBOARD_KEY,
  DEFAULT_DASHBOARD_KEY,
  getDashboardDisplayName,
  getDashboardKey,
} from '@/utils/dashboardKeyUtils'
import React from 'react'
import { Link } from 'react-router-dom'

interface FactorAnalyticsTopNavProps {
  canManage: boolean
  canShowAttribution: boolean
  canShowRisk: boolean
  currentPath?: string
  dashboards: readonly Dashboard[]
  isAdminAuthorized: boolean
  isDarkMode: boolean
  onDarkModeToggle: (isDarkMode: boolean) => void
  selectedDashboardKey: string
}

// Every dashboard — including Default — gets an explicit dashboardKey param, so
// switching between any two links always changes the URL and RADashboardPage can
// detect it
const dashboardLinkTo = (dashboardKey: string): string =>
  `/factor-analytics?dashboardKey=${encodeURIComponent(dashboardKey)}`

// Public dashboards are shared with everyone — including a user's own public
// dashboards, since editing one changes it for everyone else too.
// All of a user's created dashboards are personal.
const partitionByVisibility = (
  dashboardList: readonly Dashboard[],
): { personal: Dashboard[]; public: Dashboard[] } => {
  const personal: Dashboard[] = []
  const shared: Dashboard[] = []
  //dashboardList.forEach((d) => (d.isPrivate ? personal : shared).push(d))
  dashboardList.forEach((d) => {
    if (d.isPrivate || d.currentUserOwned) personal.push(d);
    if (!d.isPrivate) shared.push(d)
  })
  return { personal, public: shared }
}

// Renders a literal <TopNavigation.Link> per dashboard, inline as direct JSX
// children — TopNavigation parses its children structurally (it never renders
// child components to discover them), so this can't be a wrapper component.
const renderDashboardLinks = (
  dashboardsToRender: readonly Dashboard[],
  dashboards: readonly Dashboard[],
  selectedDashboardKey: string,
): React.ReactElement[] =>
  dashboardsToRender.map((d) => {
    const dashboardKey = getDashboardKey(d)
    return (
      <TopNavigation.Link
        key={dashboardKey}
        isActive={selectedDashboardKey === dashboardKey}
        to={dashboardLinkTo(dashboardKey)}
      >
        {getDashboardDisplayName(dashboardKey, dashboards)}
      </TopNavigation.Link>
    )
  })

const FactorAnalyticsTopNav = ({
  canManage,
  canShowAttribution,
  canShowRisk,
  currentPath = '/factor-analytics',
  dashboards,
  isAdminAuthorized,
  isDarkMode,
  onDarkModeToggle,
  selectedDashboardKey,
}: FactorAnalyticsTopNavProps): React.ReactElement => {
  // Partition by type in a single pass; anything not Attribution falls back to Risk.
  const riskDashboards: Dashboard[] = []
  const attributionDashboards: Dashboard[] = []
  dashboards.forEach((d) => {
    switch (d.dashboardType) {
      case FADashboardType.Attribution:
        attributionDashboards.push(d)
        break
      case FADashboardType.Risk:
      default:
        riskDashboards.push(d)
        break
    }
  })

  const risk = partitionByVisibility(riskDashboards)
  const attribution = partitionByVisibility(attributionDashboards)

  return (
    <div style={{ padding: '12px 40px 0', marginBottom: '30px' }}>
      <TopNavigation
        currentPath={currentPath}
        linkComponent={Link}
        utilityActions={[
          {
            ariaLabel: isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode',
            icon: <DarkModeIcon isDarkMode={isDarkMode} />,
            onClick: () => onDarkModeToggle(!isDarkMode),
          },
        ]}
      >
        <TopNavigation.Brand to="/factor-analytics">Factor Analytics</TopNavigation.Brand>
        {/* canShowRisk is currently always true wherever this component mounts:
            RISK_MIN_SCHEMA_VERSION === FACTOR_ANALYTICS_MIN_SCHEMA_VERSION, and
            RADashboardPage already renders the version-mismatch fallback instead
            of this nav whenever !isFactorAnalyticsEnabled. The disabled/tooltip
            wiring below is unreachable today — kept in case the two thresholds
            are ever meant to diverge (Risk requiring a later version than the
            page itself). */}
        <TopNavigation.Menu
          disabled={!canShowRisk}
          disabledTooltip="Axiom System Update Required"
          label="Risk"
        >
          <TopNavigation.Section key="risk-public" label="Public">
            {/** Default Dashboard First */}
            <TopNavigation.Link
              isActive={selectedDashboardKey === DEFAULT_DASHBOARD_KEY}
              to={dashboardLinkTo(DEFAULT_DASHBOARD_KEY)}
            >
              {getDashboardDisplayName(DEFAULT_DASHBOARD_KEY, dashboards)}
            </TopNavigation.Link>
            {/** Rest of the Public Dashboards */}
            {renderDashboardLinks(risk.public, dashboards, selectedDashboardKey)}
          </TopNavigation.Section>

          {/** All user's Private Dashboards */}
          <TopNavigation.Section key="risk-personal" label="Personal">
            {renderDashboardLinks(risk.personal, dashboards, selectedDashboardKey)}
          </TopNavigation.Section>

          {canManage && (
            <TopNavigation.FooterLink to="/factor-analytics/manage-dashboards">Dashboard Manager</TopNavigation.FooterLink>
          )}

        </TopNavigation.Menu>

        
        <TopNavigation.Menu
          disabled={!canShowAttribution}
          disabledTooltip="Axiom System Update Required"
          label="Attribution"
        >
          <TopNavigation.Section key="attribution-public" label="Public">
            {/** Default Dashboard First */}
            <TopNavigation.Link
              isActive={selectedDashboardKey === DEFAULT_ATTRIBUTION_DASHBOARD_KEY}
              to={dashboardLinkTo(DEFAULT_ATTRIBUTION_DASHBOARD_KEY)}
            >
              {getDashboardDisplayName(DEFAULT_ATTRIBUTION_DASHBOARD_KEY, dashboards)}
            </TopNavigation.Link>
            {/** Rest of the Public Dashboards */}
            {renderDashboardLinks(attribution.public, dashboards, selectedDashboardKey)}
          </TopNavigation.Section>

          {/** All user's Private Dashboards */}
          <TopNavigation.Section key="attribution-personal" label="Personal">
            {renderDashboardLinks(attribution.personal, dashboards, selectedDashboardKey)}
          </TopNavigation.Section>

          {canManage && (
            <TopNavigation.FooterLink to="/factor-analytics/manage-dashboards">Dashboard Manager</TopNavigation.FooterLink>
          )}
        </TopNavigation.Menu>


        {canManage && (
          <TopNavigation.Link to="/factor-analytics/manage-dashboards">Dashboard Manager</TopNavigation.Link>
        )}
        {isAdminAuthorized && (
          <TopNavigation.Link to="/factor-analytics/report-schedules">Report Schedules</TopNavigation.Link>
        )}
      </TopNavigation>
    </div>
  )
}

export default FactorAnalyticsTopNav
